import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Modal, ProgressBar, Row, Table } from 'react-bootstrap';
import { useAlert } from '../../providers/AlertProvider';
import { ActivityIndicator } from './ActivityIndicator';
import { getSessionUUID } from '../lib/AuthToken';
import logger from '../../utils/logger';
import '../css/StockTakeModal.css';

const StockTakeManagement = ({ theme, customers, selectedCustomerId = null, isCustomer, profile }) => {
    const { showAlert } = useAlert();
    const [isLoading, setIsLoading] = useState(false);
    const [stockTakes, setStockTakes] = useState([]);
    const [currentStockTake, setCurrentStockTake] = useState(null);
    const [stockTakeItems, setStockTakeItems] = useState([]);
    const [auditEvents, setAuditEvents] = useState([]);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancellationReason, setCancellationReason] = useState('');
    const [scope, setScope] = useState('customer');
    const [selectedCustomer, setSelectedCustomer] = useState('');

    useEffect(() => {
        if (selectedCustomerId) {
            setSelectedCustomer(String(selectedCustomerId));
        } else if (isCustomer && profile?.customer_id) {
            setSelectedCustomer(String(profile.customer_id));
        }
    }, [selectedCustomerId, isCustomer, profile?.customer_id]);

    const request = async (url, options = {}) => {
        const sessionToken = await getSessionUUID();
        const response = await fetch(url, {
            ...options,
            credentials: 'include',
            headers: {
                ...(options.body ? { 'Content-Type': 'application/json' } : {}),
                Authorization: 'Bearer ' + sessionToken,
                ...options.headers,
            },
        });
        const body = await response.json();
        if (!response.ok) {
            throw new Error(body.error || 'Stock take request failed');
        }
        return body;
    };

    const showError = (error) => {
        logger.error('Stock take error:', error);
        showAlert({
            message: error.message || 'Stock take request failed',
            type: 'danger',
            buttons: [{ label: 'Close', type: 'primary' }],
        });
    };

    const loadStockTakes = async () => {
        setIsLoading(true);
        try {
            setStockTakes(await request('/enhanced_warehouse/stock_takes'));
        } catch (error) {
            showError(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadStockTakes();
        // The authenticated account changes only when this component is remounted.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const createStockTake = async () => {
        if (scope === 'customer' && !selectedCustomer) {
            showError(new Error('Select a customer for this stock take'));
            return;
        }
        setIsLoading(true);
        try {
            const result = await request('/enhanced_warehouse/stock_takes', {
                method: 'POST',
                body: JSON.stringify({
                    scope,
                    customer_id: scope === 'customer' ? Number(selectedCustomer) : null,
                }),
            });
            setShowCreateModal(false);
            showAlert({
                message: `${result.message}. ${result.items_count} inventory lines captured.`,
                type: 'success',
                buttons: [{ label: 'Close', type: 'primary' }],
            });
            await loadStockTakes();
        } catch (error) {
            showError(error);
            setIsLoading(false);
        }
    };

    const loadDetails = async (stockTakeId) => {
        setIsLoading(true);
        try {
            const data = await request(`/enhanced_warehouse/stock_takes/${stockTakeId}`);
            setCurrentStockTake(data.stock_take);
            setStockTakeItems(data.items || []);
            setAuditEvents(data.audit_events || []);
        } catch (error) {
            showError(error);
        } finally {
            setIsLoading(false);
        }
    };

    const cancelStockTake = async () => {
        const reason = cancellationReason.trim();
        if (!reason) {
            showError(new Error('Enter a reason for cancelling this stock take'));
            return;
        }
        setIsLoading(true);
        try {
            const result = await request(
                `/enhanced_warehouse/stock_takes/${currentStockTake.id}/cancel`,
                {
                    method: 'POST',
                    body: JSON.stringify({ reason }),
                }
            );
            setShowCancelModal(false);
            setCancellationReason('');
            await loadDetails(currentStockTake.id);
            await loadStockTakes();
            showAlert({
                message: result.message,
                type: 'success',
                buttons: [{ label: 'Close', type: 'primary' }],
            });
        } catch (error) {
            showError(error);
            setIsLoading(false);
        }
    };

    const applyStockAdjustments = () => {
        showAlert({
            message: 'Apply the reviewed variances to live warehouse stock? This action is transactional, writes each adjustment to warehouse_log, and cannot be undone through the stock take.',
            type: 'warning',
            buttons: [
                { label: 'Keep Reviewing', type: 'secondary' },
                {
                    label: 'Apply Stock Adjustments',
                    type: 'danger',
                    onClick: async () => {
                        setIsLoading(true);
                        try {
                            const result = await request(
                                `/enhanced_warehouse/stock_takes/${currentStockTake.id}/apply_adjustments`,
                                {
                                    method: 'POST',
                                    body: JSON.stringify({}),
                                }
                            );
                            await loadDetails(currentStockTake.id);
                            await loadStockTakes();
                            showAlert({
                                message: result.message,
                                type: 'success',
                                buttons: [{ label: 'Close', type: 'primary' }],
                            });
                        } catch (error) {
                            showError(error);
                            setIsLoading(false);
                        }
                    },
                },
            ],
        });
    };

    const countedItems = useMemo(
        () => stockTakeItems.filter((item) => item.actual_quantity !== null).length,
        [stockTakeItems]
    );
    const progress = stockTakeItems.length
        ? Math.round((countedItems / stockTakeItems.length) * 100)
        : 0;
    const bulkGroups = useMemo(() => {
        const groups = new Map();
        stockTakeItems.forEach((item) => {
            if (!item.is_bulk_location || !item.location_group) {
                return;
            }
            const key = [
                item.location_group,
                item.customer_id ?? 'unassigned',
                item.product_id,
                item.expected_quantity,
            ].join('|');
            const group = groups.get(key);
            if (group) {
                group.items.push(item);
            } else {
                groups.set(key, {
                    key,
                    location: item.location_group,
                    customerName: item.customer_name,
                    productCode: item.product_code,
                    productName: item.product_name,
                    quantityPerPallet: item.expected_quantity,
                    items: [item],
                });
            }
        });
        return Array.from(groups.values());
    }, [stockTakeItems]);

    const formatDateTime = (value) => value
        ? new Date(value).toLocaleString('en-GB')
        : 'Not yet';

    const statusBadge = (status) => {
        const variants = {
            requested: 'warning',
            in_progress: 'primary',
            awaiting_approval: 'info',
            completed: 'success',
            cancelled: 'danger',
        };
        return (
            <span className={`badge bg-${variants[status] || 'secondary'}`}>
                {(status || '').replace('_', ' ').toUpperCase()}
            </span>
        );
    };

    if (isLoading && !currentStockTake && stockTakes.length === 0) {
        return <div className="d-flex justify-content-center mt-5"><ActivityIndicator /></div>;
    }

    return (
        <div className="stock-take-management" id={theme}>
            {!currentStockTake ? (
                <>
                    <Row className="mb-4 align-items-center">
                        <Col>
                            <h4>Stock Take Requests</h4>
                            <div className="text-muted">
                                Request and review checks here. Counting and completion are mobile-only.
                            </div>
                        </Col>
                        {!isCustomer && (
                            <Col xs="auto">
                                <Button onClick={() => setShowCreateModal(true)} disabled={isLoading}>
                                    Request Stock Take
                                </Button>
                            </Col>
                        )}
                    </Row>
                    <Card className={theme === 'dark' ? 'bg-dark text-light' : ''}>
                        <Card.Body>
                            {stockTakes.length === 0 ? (
                                <Alert variant="info">No stock take requests found.</Alert>
                            ) : (
                                <Table striped bordered hover responsive variant={theme === 'dark' ? 'dark' : undefined}>
                                    <thead>
                                        <tr>
                                            <th>Reference</th>
                                            <th>Scope</th>
                                            <th>Requested</th>
                                            <th>Requested By</th>
                                            <th>Status</th>
                                            <th>Items</th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {stockTakes.map((stockTake) => (
                                            <tr key={stockTake.id}>
                                                <td>{stockTake.reference_number}</td>
                                                <td>{stockTake.customer_name}</td>
                                                <td>{formatDateTime(stockTake.requested_at)}</td>
                                                <td>{stockTake.requested_by || 'Unknown'}</td>
                                                <td>{statusBadge(stockTake.status)}</td>
                                                <td>{stockTake.items_count}</td>
                                                <td>
                                                    <Button size="sm" variant="outline-primary" onClick={() => loadDetails(stockTake.id)}>
                                                        View Check
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            )}
                        </Card.Body>
                    </Card>
                </>
            ) : (
                <>
                    <div className="d-flex justify-content-between align-items-start mb-3">
                        <div className="text-start">
                            <h4>{currentStockTake.reference_number}</h4>
                            <div>{currentStockTake.customer_name} | {statusBadge(currentStockTake.status)}</div>
                            <small className="text-muted">
                                Snapshot: {formatDateTime(currentStockTake.snapshot_at)}
                            </small>
                        </div>
                        <div className="d-flex gap-2">
                            {profile?.is_super_admin && currentStockTake.status === 'awaiting_approval' && (
                                <Button
                                    variant="danger"
                                    onClick={applyStockAdjustments}
                                    disabled={isLoading}
                                >
                                    Apply Stock Adjustments
                                </Button>
                            )}
                            {!isCustomer && ['requested', 'in_progress'].includes(currentStockTake.status) && (
                                <Button
                                    variant="outline-danger"
                                    onClick={() => setShowCancelModal(true)}
                                    disabled={isLoading}
                                >
                                    Cancel Stock Take
                                </Button>
                            )}
                            <Button variant="secondary" onClick={() => setCurrentStockTake(null)}>Back</Button>
                        </div>
                    </div>

                    <Card className={`mb-3 ${theme === 'dark' ? 'bg-dark text-light' : ''}`}>
                        <Card.Body>
                            <div className="d-flex justify-content-between">
                                <span>{countedItems} of {stockTakeItems.length} lines counted</span>
                                <span>{progress}%</span>
                            </div>
                            <ProgressBar now={progress} variant={progress === 100 ? 'success' : 'primary'} />
                        </Card.Body>
                    </Card>

                    {currentStockTake.status === 'awaiting_approval' && (
                        <Alert variant="warning">
                            Mobile counting is complete and locked. A super admin must review the
                            variances and choose <strong>Apply Stock Adjustments</strong> before any
                            warehouse stock levels change.
                        </Alert>
                    )}

                    {bulkGroups.length > 0 && (
                        <Card className={`mb-3 ${theme === 'dark' ? 'bg-dark text-light' : ''}`}>
                            <Card.Header>Bulk Pallet Checks</Card.Header>
                            <Card.Body>
                                <Alert variant="info">
                                    Bulk rows are grouped only when location, customer, SKU, and quantity per pallet match.
                                    Pallet-level evidence remains in the detailed check below.
                                </Alert>
                                <Table striped bordered responsive variant={theme === 'dark' ? 'dark' : undefined}>
                                    <thead>
                                        <tr>
                                            <th>Bulk Location</th>
                                            {currentStockTake.scope === 'warehouse' && <th>Customer</th>}
                                            <th>Product</th>
                                            <th>Quantity / Pallet</th>
                                            <th>Pallets</th>
                                            <th>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {bulkGroups.map((group) => {
                                            const counted = group.items.filter((item) => item.actual_quantity !== null).length;
                                            return (
                                                <tr key={group.key}>
                                                    <td>{group.location}</td>
                                                    {currentStockTake.scope === 'warehouse' && <td>{group.customerName || 'Unassigned'}</td>}
                                                    <td>{group.productCode} - {group.productName}</td>
                                                    <td>{group.quantityPerPallet}</td>
                                                    <td>{group.items.length}</td>
                                                    <td>{counted === group.items.length ? 'Confirmed' : `${counted} / ${group.items.length} checked`}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    )}

                    <Card className={`mb-3 ${theme === 'dark' ? 'bg-dark text-light' : ''}`}>
                        <Card.Header>Stock Check (location order)</Card.Header>
                        <Card.Body>
                            <Table striped bordered hover responsive variant={theme === 'dark' ? 'dark' : undefined}>
                                <thead>
                                    <tr>
                                        <th>Location</th>
                                        {currentStockTake.scope === 'warehouse' && <th>Customer</th>}
                                        <th>Product</th>
                                        <th>Expected</th>
                                        <th>Actual</th>
                                        <th>Variance</th>
                                        <th>Counted By</th>
                                        <th>Counted At</th>
                                        <th>Notes</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stockTakeItems.map((item) => {
                                        const variance = item.actual_quantity === null
                                            ? null
                                            : item.actual_quantity - item.expected_quantity;
                                        return (
                                            <tr key={item.id}>
                                                <td>{item.pallet_location}</td>
                                                {currentStockTake.scope === 'warehouse' && <td>{item.customer_name}</td>}
                                                <td>{item.product_code} - {item.product_name}</td>
                                                <td>{item.expected_quantity}</td>
                                                <td>{item.actual_quantity ?? 'Pending'}</td>
                                                <td>{variance === null ? '-' : variance}</td>
                                                <td>{item.counted_by || '-'}</td>
                                                <td>{formatDateTime(item.counted_at)}</td>
                                                <td>{item.notes || '-'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>

                    <Card className={theme === 'dark' ? 'bg-dark text-light' : ''}>
                        <Card.Header>Audit Trail</Card.Header>
                        <Card.Body>
                            <Table striped bordered responsive variant={theme === 'dark' ? 'dark' : undefined}>
                                <thead><tr><th>Time</th><th>Event</th><th>User</th><th>Evidence</th></tr></thead>
                                <tbody>
                                    {auditEvents.map((event) => (
                                        <tr key={event.id}>
                                            <td>{formatDateTime(event.occurred_at)}</td>
                                            <td>{event.event_type.replace('_', ' ')}</td>
                                            <td>{event.actor_name}</td>
                                            <td><code>{JSON.stringify(event.details)}</code></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </>
            )}

            <Modal
                show={showCreateModal}
                onHide={() => setShowCreateModal(false)}
                centered
                className={`stock-take-request-modal ${theme === 'dark' ? 'dark' : ''}`}
                backdropClassName="stock-take-request-backdrop"
            >
                <Modal.Header closeButton><Modal.Title>Request Stock Take</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Scope</Form.Label>
                        <Form.Select value={scope} onChange={(event) => setScope(event.target.value)}>
                            <option value="customer">Individual customer</option>
                            <option value="warehouse">Whole warehouse</option>
                        </Form.Select>
                    </Form.Group>
                    {scope === 'customer' && (
                        <Form.Group>
                            <Form.Label>Customer</Form.Label>
                            <Form.Select value={selectedCustomer} onChange={(event) => setSelectedCustomer(event.target.value)}>
                                <option value="">Select customer</option>
                                {(customers || []).map((customer) => (
                                    <option key={customer.id} value={customer.id}>
                                        {customer.company}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    )}
                    {scope === 'warehouse' && (
                        <Alert variant="warning" className="mt-3 mb-0">
                            This captures all current warehouse inventory and presents it in location order.
                        </Alert>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                    <Button onClick={createStockTake} disabled={isLoading}>Create Request</Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showCancelModal}
                onHide={() => !isLoading && setShowCancelModal(false)}
                centered
                className={`stock-take-request-modal ${theme === 'dark' ? 'dark' : ''}`}
                backdropClassName="stock-take-request-backdrop"
            >
                <Modal.Header closeButton={!isLoading}>
                    <Modal.Title>Cancel Stock Take</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Alert variant="warning">
                        The request will be cancelled, not deleted. Its snapshot and audit history will remain available.
                    </Alert>
                    <Form.Group>
                        <Form.Label>Cancellation reason</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            maxLength={1000}
                            value={cancellationReason}
                            onChange={(event) => setCancellationReason(event.target.value)}
                            placeholder="Why is this stock take being cancelled?"
                            disabled={isLoading}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCancelModal(false)} disabled={isLoading}>
                        Keep Stock Take
                    </Button>
                    <Button variant="danger" onClick={cancelStockTake} disabled={isLoading || !cancellationReason.trim()}>
                        {isLoading ? 'Cancelling...' : 'Cancel Stock Take'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default StockTakeManagement;
